import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import api, { adminApi } from '../services/api';
import { translations } from '../components/LanguageSelector/translations';
import ShippingInfoModal from '../components/ShippingInfoModal';
import ShippingErrorModal from '../components/ShippingErrorModal';

interface CartItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    stock: number;
    imageUrl: string | null;
    slug: string | null;
    category: {
      id: string;
      name: string;
      slug: string;
    };
  };
}

interface ShippingCost {
  id: string;
  nameSlug: string;
  cost: number;
  minOrderAmount: number | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  countries: string[];
}

interface Cart {
  id: string;
  items: CartItem[];
}

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [selectedShippingCost, setSelectedShippingCost] = useState<ShippingCost | null>(null);
  const [updatingItem, setUpdatingItem] = useState<string | null>(null);
  const [shippingInfoModal, setShippingInfoModal] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
  }>({
    isOpen: false,
    title: '',
    description: ''
  });
  const [shippingAddress, setShippingAddress] = useState({
    address: user?.address || '',
    city: user?.city || '',
    postalCode: user?.postalCode || '',
    country: user?.country || 'NL'
  });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [removingItem, setRemovingItem] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [shippingErrorModal, setShippingErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    allowedCountries: string[];
  }>({
    isOpen: false,
    title: '',
    message: '',
    allowedCountries: []
  });

  const t = translations[language] || translations.en;

  const getProductImage = (product: CartItem['product']) => {
    if (!product.category || !product.slug) return null;
    
    // Generate path for _1_small.jpg image
    const imagePath = `/products/${product.category.slug}/${product.slug}/${product.category.slug}_1_small.jpg`;
    return imagePath;
  };

  const calculateShippingCost = () => {
    if (!cart || !selectedShippingCost) return 0;
    
    const subtotal = cart.items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    
    // Check if order meets minimum amount for free shipping
    if (selectedShippingCost.minOrderAmount && subtotal >= selectedShippingCost.minOrderAmount) {
      return 0;
    }
    
    return selectedShippingCost.cost;
  };

  const getTotalWithShipping = () => {
    if (!cart) return 0;
    const subtotal = cart.items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    return subtotal + calculateShippingCost();
  };

  const isShippingAddressValid = () => {
    if (!selectedShippingCost) return true;
    
    const isCountrySupported = selectedShippingCost.countries.includes(shippingAddress.country.toUpperCase());
    return isCountrySupported;
  };

  const checkShippingAddressValidity = () => {
    if (!selectedShippingCost) return true;
    
    const isCountrySupported = selectedShippingCost.countries.includes(shippingAddress.country.toUpperCase());
    
    if (!isCountrySupported) {
      const countryNames: { [key: string]: string } = {
        'NL': 'Netherlands',
        'BE': 'Belgium', 
        'DE': 'Germany',
        'FR': 'France',
        'IT': 'Italy'
      };
      
      const allowedCountryNames = selectedShippingCost.countries.map(code => countryNames[code] || code);
      
      setShippingErrorModal({
        isOpen: true,
        title: t.cart.shippingNotAvailable || 'Shipping Not Available',
        message: t.cart.shippingNotAvailableMessage || 'We cannot ship to the selected address. Please choose a different address or contact us for assistance.',
        allowedCountries: allowedCountryNames
      });
      
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCart();
    loadShippingCosts();
    
    // If user has no complete address in profile, automatically show the form
    if (!user?.address || !user?.city || !user?.postalCode) {
      setShowAddressForm(true);
      // Initialize with empty values
      setShippingAddress({
        address: '',
        city: '',
        postalCode: '',
        country: user?.country || 'NL'
      });
    } else {
      // If user has a complete address, set it as shipping address
      setShippingAddress({
        address: user.address,
        city: user.city,
        postalCode: user.postalCode,
        country: user.country || 'NL'
      });
    }
    

  }, [user, navigate]);

  // Check shipping address validity whenever shipping cost or address changes
  useEffect(() => {
    if (selectedShippingCost && shippingAddress.country) {
      checkShippingAddressValidity();
    }
  }, [selectedShippingCost, shippingAddress.country]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (err) {
      setError(t.cart.errorLoading || 'Error loading cart');
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadShippingCosts = async () => {
    try {
      const response = await adminApi.getPublicShippingCosts();
      setShippingCosts(response);
      
      // Set default shipping cost
      const defaultShipping = response.find((cost: ShippingCost) => cost.isDefault);
      if (defaultShipping) {
        setSelectedShippingCost(defaultShipping);
      }
    } catch (error) {
      console.error('Error loading shipping costs:', error);
    }
  };

  const loadShippingCostsForCountry = async (country: string) => {
    try {
      const response = await adminApi.getPublicShippingCosts();
      
      // Filter costs for this country
      const filteredCosts = response.filter((cost: ShippingCost) => 
        cost.countries.includes(country.toUpperCase())
      );
      
      // Keep all shipping costs in state but set the appropriate one for this country
      setShippingCosts(response);
      
      // Set default shipping cost for this country
      const defaultShipping = filteredCosts.find((cost: ShippingCost) => cost.isDefault);
      if (defaultShipping) {
        setSelectedShippingCost(defaultShipping);
      } else if (filteredCosts.length > 0) {
        setSelectedShippingCost(filteredCosts[0]);
      } else {
        setSelectedShippingCost(null);
      }
    } catch (error) {
      console.error('Error loading shipping costs for country:', error);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (quantity < 1) return;

    try {
      setUpdatingItem(itemId);
      await api.put(`/cart/update/${itemId}`, { quantity });
      await fetchCart(); // Refresh cart
          } catch (err: any) {
        const errorMessage = err.response?.data?.error || t.cart.errorUpdating || 'Error updating quantity';
        
        // Check if it's a stock issue
        if (errorMessage.includes('Not enough stock') || errorMessage.includes('stock')) {
          // Find the cart item to get product details
          const cartItem = cart?.items.find(item => item.id === itemId);
          if (cartItem) {
            // Notify admin about stock issue
            try {
              await api.post('/products/notify-stock-issue', {
                productId: cartItem.product.id,
                productName: cartItem.product.name,
                requestedQuantity: quantity,
                availableStock: cartItem.product.stock
              });
            } catch (notificationError) {
              console.error('Error notifying admin about stock issue:', notificationError);
            }
          }
        }
        
        // Show a more informative message for stock issues
        if (errorMessage.includes('Not enough stock') || errorMessage.includes('stock')) {
          alert(`Stock insufficiente per questo prodotto. ${errorMessage}\n\nL'amministratore è stato notificato e aggiornerà lo stock al più presto.`);
        } else {
          alert(errorMessage);
        }
      } finally {
      setUpdatingItem(null);
    }
  };

  const removeItem = async (itemId: string) => {
    try {
      setRemovingItem(itemId);
      await api.delete(`/cart/remove/${itemId}`);
      await fetchCart(); // Refresh cart
    } catch (err) {
      alert(t.cart.errorRemoving || 'Error removing item');
    } finally {
      setRemovingItem(null);
    }
  };

  const clearCart = async () => {
    if (!confirm(t.cart.confirmClear || 'Are you sure you want to clear your cart?')) {
      return;
    }

    try {
      await api.delete('/cart/clear');
      await fetchCart(); // Refresh cart
    } catch (err) {
      alert(t.cart.errorClearing || 'Error clearing cart');
    }
  };

  const proceedToCheckout = async () => {
    if (!cart || cart.items.length === 0) {
      alert(t.cart.emptyCart || 'Your cart is empty');
      return;
    }

    // Validate shipping address
    if (!shippingAddress.address || !shippingAddress.city || !shippingAddress.postalCode) {
      alert('Please fill in your shipping address');
      return;
    }

    // Check if shipping is available to the selected address
    if (!checkShippingAddressValidity()) {
      return; // The modal will be shown by checkShippingAddressValidity
    }

    try {
      setCheckoutLoading(true);
      const response = await api.post('/product-orders/create-checkout-session', {
        shippingAddress: shippingAddress.address,
        shippingCity: shippingAddress.city,
        shippingPostalCode: shippingAddress.postalCode,
        shippingCountry: shippingAddress.country
      });

      // Redirect to Stripe checkout
      const stripe = (window as any).Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId
      });

      if (error) {
        alert(t.cart.checkoutError || 'Error redirecting to checkout');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t.cart.checkoutError || 'Error creating checkout session';
      
      // Check if it's a stock issue
      if (errorMessage.includes('Not enough stock') || errorMessage.includes('stock')) {
        // Extract product name from error message
        const stockErrorMatch = errorMessage.match(/Not enough stock for (.+?)\./);
        if (stockErrorMatch && cart) {
          const productName = stockErrorMatch[1];
          const cartItem = cart.items.find(item => item.product.name === productName);
          if (cartItem) {
            // Notify admin about stock issue
            try {
              await api.post('/products/notify-stock-issue', {
                productId: cartItem.product.id,
                productName: cartItem.product.name,
                requestedQuantity: cartItem.quantity,
                availableStock: cartItem.product.stock
              });
            } catch (notificationError) {
              console.error('Error notifying admin about stock issue:', notificationError);
            }
          }
        }
      }
      
      // Show a more informative message for stock issues
      if (errorMessage.includes('Not enough stock') || errorMessage.includes('stock')) {
        alert(`Stock insufficiente per questo prodotto. ${errorMessage}\n\nL'amministratore è stato notificato e aggiornerà lo stock al più presto.`);
      } else {
        alert(errorMessage);
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!cart) return 0;
    return cart.items.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0);
  };

  const calculateItemCount = () => {
    if (!cart) return 0;
    return cart.items.reduce((count, item) => {
      return count + item.quantity;
    }, 0);
  };

  if (!user) {
    return null; // Will redirect to login
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t.common.loading || 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={fetchCart}
              className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {t.common.retry || 'Retry'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t.cart.title || 'Shopping Cart'}
          </h1>
          <p className="text-gray-600">
            {t.cart.subtitle || 'Review your items and proceed to checkout'}
          </p>
        </div>

        {!cart || cart.items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.cart.emptyTitle || 'Your cart is empty'}
            </h3>
            <p className="text-gray-600 mb-6">
              {t.cart.emptySubtitle || 'Add some products to get started'}
            </p>
            <button
              onClick={() => navigate('/products')}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              {t.cart.browseProducts || 'Browse Products'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t.cart.items || 'Items'} ({calculateItemCount()})
                    </h2>
                    <button
                      onClick={clearCart}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      {t.cart.clearCart || 'Clear Cart'}
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {cart.items.map((item) => (
                    <div key={item.id} className="p-6">
                      <div className="flex items-center">
                        {/* Product Image */}
                        <div className="flex-shrink-0 w-20 h-20">
                          {getProductImage(item.product) ? (
                            <img
                              src={getProductImage(item.product) || undefined}
                              alt={item.product.name}
                              className="w-full h-full object-cover rounded-md"
                              onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full bg-gray-200 rounded-md flex items-center justify-center ${getProductImage(item.product) ? 'hidden' : ''}`}>
                            <span className="text-gray-400 text-xs">{t.products.noImage || 'No image'}</span>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="ml-4 flex-1">
                          <h3 className="text-lg font-medium text-gray-900">
                            {item.product.name}
                          </h3>
                          <p className="text-lg font-semibold text-green-600 mt-2">
                            €{item.product.price.toFixed(2)}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={updatingItem === item.id || item.quantity <= 1}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                          >
                            -
                          </button>
                          <span className="w-12 text-center font-medium">
                            {updatingItem === item.id ? '...' : item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            disabled={updatingItem === item.id || item.quantity >= item.product.stock}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                          >
                            +
                          </button>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={removingItem === item.id}
                          className="ml-4 text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {removingItem === item.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.cart.orderSummary || 'Order Summary'}
                </h2>

                <div className="space-y-3 mb-6">
                  {/* Subtotal */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.cart.subtotal || 'Subtotal'}</span>
                    <span className="font-medium">€{calculateTotal().toFixed(2)}</span>
                  </div>

                  {/* Shipping Cost */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">{t.cart.shipping || 'Shipping'}</span>
                      {selectedShippingCost && (
                        <button
                          type="button"
                          onClick={() => {
                            const title = t.shipping[selectedShippingCost.nameSlug as keyof typeof t.shipping];
                            const description = t.shipping[selectedShippingCost.nameSlug + '_desc' as keyof typeof t.shipping];
                            setShippingInfoModal({
                              isOpen: true,
                              title,
                              description
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title={t.cart.shippingInfo || 'Shipping information'}
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <span className="font-medium">
                      {calculateShippingCost() === 0 ? (t.cart.freeShipping || 'Free') : `€${calculateShippingCost().toFixed(2)}`}
                    </span>
                  </div>

                  {/* Products Summary */}
                  <div className="border-t border-gray-200 pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t.cart.productsSummary || 'Products'}</h4>
                    <div className="space-y-2">
                      {cart?.items.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <div className="flex-1">
                            <span className="text-gray-900">{item.product.name}</span>
                            <span className="text-gray-500 ml-1">× {item.quantity}</span>
                          </div>
                          <span className="text-gray-600">€{(item.product.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping Cost Selection - Only show if multiple options available */}
                  {shippingCosts.length > 1 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t.cart.selectShipping || 'Select Shipping Method'}
                        </label>
                      </div>
                      <select
                        value={selectedShippingCost?.id || ''}
                        onChange={(e) => {
                          const selected = shippingCosts.find(cost => cost.id === e.target.value);
                          setSelectedShippingCost(selected || null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {shippingCosts.map((cost) => (
                          <option key={cost.id} value={cost.id}>
                            {t.shipping[cost.nameSlug as keyof typeof t.shipping]}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Total */}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">{t.cart.total || 'Total'}</span>
                      <span className="text-lg font-semibold text-green-600">€{getTotalWithShipping().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={proceedToCheckout}
                  disabled={
                    checkoutLoading || 
                    cart.items.length === 0 || 
                    !(shippingAddress.address && shippingAddress.city && shippingAddress.postalCode) ||
                    !isShippingAddressValid()
                  }
                  className={`w-full py-3 px-4 rounded-md font-medium transition-colors duration-200 ${
                    checkoutLoading || 
                    cart.items.length === 0 || 
                    !(shippingAddress.address && shippingAddress.city && shippingAddress.postalCode) ||
                    !isShippingAddressValid()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {checkoutLoading 
                    ? (t.cart.processing || 'Processing...')
                    : (t.cart.proceedToCheckout || 'Proceed to Checkout')
                  }
                </button>

                <p className="text-xs text-gray-500 mt-3 text-center">
                  {t.cart.secureCheckout || 'Secure checkout powered by Stripe'}
                </p>

                {/* Shipping Address - Moved after checkout button */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t.cart.shippingAddress || 'Shipping Address'}
                    </label>
                    {/* Only show Edit/Hide button if there's a valid address */}
                    {(shippingAddress.address && shippingAddress.city && shippingAddress.postalCode) && (
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(!showAddressForm)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                      >
                        {showAddressForm ? t.cart.hide || 'Hide' : t.cart.editShippingAddress || 'Edit'}
                      </button>
                    )}
                  </div>
                  
                  {/* Show warning if country is not supported */}
                  {selectedShippingCost && !isShippingAddressValid() && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start space-x-2">
                        <svg className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">
                            {t.cart.shippingNotAvailable || 'Shipping Not Available'}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {t.cart.shippingNotAvailableMessage || 'We cannot ship to the selected address. Please choose a different address or contact us for assistance.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Only show address preview if there's a valid address and form is not open */}
                  {!showAddressForm && (shippingAddress.address && shippingAddress.city && shippingAddress.postalCode) ? (
                    <div className="text-sm text-gray-600">
                      <div className="bg-gray-50 p-3 rounded-md">
                        <div className="flex items-start space-x-2">
                          <svg className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{shippingAddress.address}</p>
                            <p className="text-gray-600">{shippingAddress.city} {shippingAddress.postalCode}</p>
                            <p className="text-gray-500 text-xs">{shippingAddress.country}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t.cart.address || 'Address'}
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.address}
                          onChange={(e) => setShippingAddress(prev => ({ ...prev, address: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Via Roma 123"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t.cart.city || 'City'}
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.city}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Amsterdam"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t.cart.postalCode || 'Postal Code'}
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.postalCode}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="1234 AB"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t.cart.country || 'Country'}
                        </label>
                                                  <select
                            value={shippingAddress.country}
                            onChange={(e) => {
                              const newCountry = e.target.value;
                              setShippingAddress(prev => ({ ...prev, country: newCountry }));
                              loadShippingCostsForCountry(newCountry);
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                          <option value="NL">Netherlands</option>
                          <option value="BE">Belgium</option>
                          <option value="DE">Germany</option>
                          <option value="FR">France</option>
                          <option value="IT">Italy</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Shipping Info Modal */}
      <ShippingInfoModal
        isOpen={shippingInfoModal.isOpen}
        onClose={() => setShippingInfoModal(prev => ({ ...prev, isOpen: false }))}
        title={shippingInfoModal.title}
        description={shippingInfoModal.description}
      />

      {/* Shipping Error Modal */}
      <ShippingErrorModal
        isOpen={shippingErrorModal.isOpen}
        onClose={() => setShippingErrorModal(prev => ({ ...prev, isOpen: false }))}
        title={shippingErrorModal.title}
        message={shippingErrorModal.message}
        allowedCountries={shippingErrorModal.allowedCountries}
      />
    </div>
  );
};

export default CartPage;
